import { Injectable, inject } from '@angular/core';
import { DatabaseService } from '@core/services/database.service';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  EMPTY,
  filter,
  forkJoin,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  toArray,
} from 'rxjs';
import { TaskOfflineStatus } from '@core/enums/task-offline-status';
import { QueueService } from '@core/services/queue.service';
import { Task } from '@core/interfaces/items/task';
import { Tasks } from '@orm-entities/tasks';
import { Assignment as AssignmentEntity } from '@orm-entities/assignment';
import { Assignment } from '@core/interfaces/assignment';
import { convertPropertyToBoolean } from '@orm-entities/entity-property-converter';
import { TreeItemType } from '@core/enums/tree-item-type';
import { InspectionTypes } from '@core/enums/inspection-types';
import { NativeFileService } from '@shared/services/native-file.service';
import { LoggerService } from '@shared/services/logger.service';

@Injectable({
  providedIn: 'root',
})
export class DownloadedTasksService {
  #databaseService = inject(DatabaseService);
  #queueService = inject(QueueService);
  #nativeFileService = inject(NativeFileService);
  #logger = inject(LoggerService);

  public downloadedTasksByProject$: Observable<{ [projectId: string]: Task[] }>;
  public downloadedTasks$: Observable<Task[]>;
  public signedDownloadedTasks$: Observable<Task[]>;
  #resfreshDownloadedTasks$: BehaviorSubject<void>;

  public constructor() {
    this.#resfreshDownloadedTasks$ = new BehaviorSubject<void>(undefined);
    this.downloadedTasksByProject$ = this.#resfreshDownloadedTasks$.pipe(
      switchMap(() => this.getDownloadedTasksFromDatabase()),
      map(this.regroupTasksByProject),
      shareReplay(1),
    );
    this.downloadedTasks$ = this.downloadedTasksByProject$.pipe(
      map(downloadedTasksByProject => Object.values(downloadedTasksByProject).flat()),
      shareReplay(1),
    );
    this.signedDownloadedTasks$ = this.downloadedTasks$.pipe(
      map(tasks => tasks.filter(task => task.taskOfflineSignedStatus)),
      shareReplay(1),
    );
  }

  public refresh() {
    this.#resfreshDownloadedTasks$.next();
  }

  public getDownloadedTasksByProject$(projectId: string): Observable<Task[]> {
    return this.downloadedTasksByProject$.pipe(map(projectsTasks => projectsTasks[projectId] ?? []));
  }

  public getDownloadedTask$(taskId: string): Observable<Task | undefined> {
    return this.downloadedTasks$.pipe(map(tasks => tasks.find(task => task.id === taskId)));
  }

  public removeDownloadedTasks(tasksId: string[]) {
    if (!tasksId?.length) {
      return of([]);
    }
    return from(tasksId).pipe(
      concatMap(taskId =>
        forkJoin([
          this.#databaseService.cleanupDownloadedTask(taskId),
          this.#nativeFileService.removeFolderAndItsContent({ path: taskId }),
          this.#queueService.clearTaskActionsQueue(taskId),
        ]).pipe(
          filter(([removedFromDB, removedFromFolder, queueRemovedFromDB]) => removedFromDB && removedFromFolder && queueRemovedFromDB),
          map(() => taskId),
        ),
      ),
      toArray(),
      concatMap(removedTasks => (removedTasks.length ? of(removedTasks) : EMPTY)),
      tap(() => {
        this.refresh();
      }),
      catchError(error => {
        this.#logger.error('Error removing downloaded tasks', error);
        return of([]);
      }),
    );
  }

  public signDownloadedTask(taskId: string) {
    return this.#databaseService.update(Tasks, { taskOfflineSignedStatus: 1 }, { id: taskId }).pipe(
      tap(() => {
        this.refresh();
      }),
    );
  }

  private getDownloadedTasksFromDatabase(): Observable<Task[]> {
    return forkJoin([this.#databaseService.select(Tasks), this.#databaseService.select(AssignmentEntity)]).pipe(
      map(([tasks, assignments]) =>
        tasks.map(task => {
          const assignement = assignments.find(({ taskId }) => taskId === task.id);
          return this.convertTaskEntity(task, assignement);
        }),
      ),
    );
  }

  private convertAssignementEntity(assignment: AssignmentEntity): Assignment {
    return {
      id: assignment.assignedId,
      assignedUserId: assignment.userId,
      createdBy: assignment.createdBy,
      createdOnUtc: assignment.createdOnUtc as unknown as Date,
      updatedOnUtc: assignment.updatedOnUtc as unknown as Date,
      isManager: convertPropertyToBoolean(assignment.isManager),
    };
  }

  private convertTaskEntity(task: Tasks, assignment: AssignmentEntity | undefined): Task {
    return {
      id: task.id,
      jobId: task.jobId,
      hubId: task.hubId,
      name: task.name,
      description: task.description,
      status: task.status,
      duration: 0, // duration is only shown for online interface so hardcoded for mobile UI
      type: task.type as TreeItemType,
      inspectionType: task.inspectionType as InspectionTypes,
      deviceModel: task.deviceModel,
      deviceSerialNumber: task.deviceSerialNumber,
      createdBy: task.createdBy,
      createdOnUtc: task.createdOnUtc,
      updatedOnUtc: task.updatedOnUtc,
      fileCount: task.fileCount,
      progressPercent: task.progressPercent,
      assignment: assignment ? this.convertAssignementEntity(assignment) : undefined,
      taskOfflineStatus: task.taskOfflineStatus as TaskOfflineStatus,
      taskOfflineSignedStatus: convertPropertyToBoolean(task.taskOfflineSignedStatus),
      taskType: task.type,
      deletionAllowed: convertPropertyToBoolean(task.deletionAllowed),
      projectName: task.projectName,
      projectDueDate: task.projectDueDate,
      taskReportApprovalStatus: task.taskReportApprovalStatus,
    };
  }

  private regroupTasksByProject(tasks: Task[]): { [projectId: string]: Task[] } {
    return tasks.reduce((projectTasks, task) => {
      if (projectTasks[task.hubId]) {
        return { ...projectTasks, [task.hubId]: [...projectTasks[task.hubId], task] };
      }
      return { ...projectTasks, [task.hubId]: [task] };
    }, {});
  }
}
